<template>
  <button
    :class="chipHTMLClass"
    :data-test="dataTest"
    :disabled="disabled"
    @click="onClickChip">
    <SVGIcon
      v-if="leftIconName"
      :data-test="leftIconDataTest"
      :iconName="leftIconName"
      :remSize="1.25"
      class="core-chip__li core-chip__icon" />
    <ImageIcon
      v-else-if="imageSrc"
      :data-test="imageIconDataTest"
      :imageSrc="imageSrc"
      class="core-chip__li core-chip__icon" />
    <span
      class="core-chip__li core-chip__label buttons--button-01"
      :data-test="labelDataTest">
      {{ label }}
    </span>
    <button
      v-if="handlerIconName"
      :data-test="handlerDataTest"
      class="core-chip__li core-chip__handler"
      @click.stop="onClickHandler">
      <SVGIcon
        :colorClass="chipHandlerColorClass"
        :iconName="handlerIconName"
        :remSize="1.25" />
    </button>
  </button>
</template>

<script setup lang="ts">
import ImageIcon from 'frontcore/views/icons/image-icon/ImageIcon.vue'
import SVGIcon from 'frontcore/views/icons/SVGIcon.vue'
import { ChipType } from 'frontcore/views/chips/chip/types'
import { computed } from 'vue'

type Props = {
  chipType: ChipType
  dataTest?: string
  deletable?: boolean
  disabled?: boolean
  iconName?: string
  imageSrc?: string
  label: string
  open?: boolean
  selected?: boolean
}

const props =  withDefaults(defineProps<Props>(), {
  open: false,
  dataTest: 'core-chip',
  deletable: false,
  disabled: false,
  selected: false,
})

type Emits = {
  (e: 'click', event: Event): void
  (e: 'handlerClick', event: Event): void
}
const emit = defineEmits<Emits>()

const chipHTMLClass = computed<Record<string, boolean>>(() => {
  return {
    'core-chip': true,
    'core-chip--disabled': props.disabled,
    'core-chip--selected': props.selected,
    'core-chip__border': true,
    'pointer': !props.disabled,
  }
})

const imageIconDataTest = computed((): string => `${props.dataTest}-image-icon`)

const leftIconDataTest = computed((): string => `${props.dataTest}-icon`)
const leftIconName = computed((): string | undefined => {
  switch (props.chipType) {
  case ChipType.ACTION:
  case ChipType.INPUT:
    return props.iconName
  case ChipType.FILTER:
  case ChipType.DROPDOWN:
    return props.selected ? 'Check' : props.iconName
  }
})

const labelDataTest = computed((): string => `${props.dataTest}-label`)

const handlerDataTest = computed((): string => `${props.dataTest}-handler`)
const handlerIconName = computed((): string | undefined => {
  switch (props.chipType) {
  case ChipType.ACTION:
  case ChipType.FILTER:
    return
  case ChipType.INPUT:
    return props.deletable ? 'Close' : undefined
  case ChipType.DROPDOWN:
    return props.open ? 'ArrowDropUp' : 'ArrowDropDown'
  }
})

const onClickChip = (event: Event) => {
  if (props.disabled) { return }
  emit('click', event)
}

const onClickHandler = (event: Event) => {
  if (props.disabled) { return }
  emit('handlerClick', event)
}

const chipHandlerColorClass = computed(() => {
  const defaultColor = props.selected ? 'tw-fill-core-primary-30' : 'tw-fill-core-neutral-cold-30'
  return props.disabled ? 'tw-fill-core-neutral-cold-80' : defaultColor
})
</script>

<style scoped>
.core-chip__border {
  border-width: 1px;
  border-style: solid;
  border-radius: var(--s-1);
}

.core-chip {
  display: inline-flex;
  gap: var(--s-1);
  align-items: center;
  justify-content: center;
  max-width: 100%;
  cursor: pointer;
  background-color: var(--core-neutral-cold-100);
  border-color: var(--core-neutral-cold-90);
}

.core-chip:focus-visible {
  box-shadow: 0 0 0 3px var(--auxiliary-info-70), 0 0 4px var(--auxiliary-info-70);
}

.core-chip:not(.core-chip--disabled):hover {
  background-color: var(--core-primary-95);
  border-color: var(--core-primary-60);
}

.core-chip:not(.core-chip--disabled).core-chip--selected {
  background-color: var(--core-primary-90);
  border-color: var(--core-primary-90);
}

.core-chip:not(.core-chip--disabled).core-chip--selected:hover {
  background-color: var(--core-primary-80);
  border-color: var(--core-primary-80);
}

.core-chip.core-chip--disabled {
  cursor: default;
  background-color: var(--core-neutral-cold-95);
  border-color: var(--core-neutral-cold-80);
}

.core-chip.core-chip--disabled.core-chip--selected {
  background-color: var(--core-neutral-cold-90);
  border-color: var(--core-neutral-cold-90);
}

.core-chip__icon, .core-chip__handler {
  flex: 0 0 1.25rem;
}

.core-chip__icon {
  fill: var(--core-primary-50);
}

.core-chip--disabled .core-chip__icon {
  fill: var(--core-neutral-cold-60);
}

.core-chip__handler {
  fill: var(--core-neutral-cold-30);
}

.core-chip--selected:not(.core-chip--disabled) .core-chip__handler {
  fill: var(--core-primary-30);
}

.core-chip--disabled .core-chip__handler {
  fill: var(--core-neutral-cold-80);
}

.core-chip__label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 1px 0;
  color: var(--core-primary-10);
}

.core-chip--disabled .core-chip__label {
  color: var(--core-neutral-cold-40);
}

.core-chip__li:first-child.core-chip__icon {
  margin-left: calc(var(--s-2) - 1px);
}

.core-chip__li:first-child.core-chip__label {
  margin-left: calc(var(--s-3) - 1px);
}

.core-chip__li:last-child.core-chip__handler {
  margin-right: calc(var(--s-2) - 1px);
}

.core-chip__li:last-child.core-chip__label {
  margin-right: calc(var(--s-3) - 1px);
}
</style>
